<template>
  <div v-if="sampleType" class="animated fadeIn">
    <b-card>
      <b-form-group label="Test Package" label-cols="2">
        <b-button v-b-modal="testPackageModalId" block>{{
          testPackage ? testPackage.name : "View Package Options"
        }}</b-button>
        <b-modal
          :id="testPackageModalId"
          size="lg"
          :title="`Select a Test Package${
            testPackage ? `: ${testPackage.name}` : ''
          }`"
        >
          <b-form-group>
            <b-input-group prepend="Filter">
              <b-form-input v-model="tp_filter" />
            </b-input-group>
          </b-form-group>
          <div v-if="testPackageOpts.length">
            <custom-form-radio-group
              :lg="4"
              :md="4"
              :grouped="true"
              :options="testPackageOpts"
              :value="submission(submissionId).test_package_id"
              @change="updateTestPackage"
            />
          </div>
          <b-alert v-else show
            >No test packages are available for this sample type.</b-alert
          >
        </b-modal>
      </b-form-group>
      <div v-if="testPackage" :key="testPackage.id">
        <project-soil-submission-form
          v-if="sampleTypeName == 'Soil'"
          :submissionId="submissionId"
          :testPackage="testPackage"
          :addOns="addOns"
          :turnArounds="turnarounds"
          :serviceQuestions="serviceQuestions"
          :allTestPackages="allTestPackages"
        />
        <project-potato-submission-form
          v-else-if="sampleTypeName == 'Potato'"
          :submissionId="submissionId"
          :serviceQuestions="serviceQuestions"
        />
        <project-irrigation-submission-form
          v-else-if="sampleTypeName == 'Irrigation Suitability'"
          :submissionId="submissionId"
          :testPackage="testPackage"
          :managedLists="managedLists"
          :serviceQuestions="serviceQuestions"
        />
        <project-tissue-submission-form
          v-else-if="sampleTypeName == 'Tissue'"
          :submissionId="submissionId"
          :addOns="addOns"
          :turnArounds="turnarounds"
          :managedLists="managedLists"
          :serviceQuestions="serviceQuestions"
        />
        <project-feed-mixer-submission-form
          v-else-if="sampleTypeName == 'Feed Mixer Efficiency'"
          :submissionId="submissionId"
          :testPackage="testPackage"
          :serviceQuestions="serviceQuestions"
          :turnArounds="turnarounds"
        />
        <project-feeds-submission-form
          v-else-if="sampleTypeName == 'Feed'"
          :submissionId="submissionId"
          :testPackageSamples="allTestPackages"
          :addOns="addOns"
          :turnArounds="turnarounds"
          :managedLists="managedLists"
          :serviceQuestions="serviceQuestions"
        />
        <project-septic-submission-form
          v-else-if="sampleTypeName == 'Septic Soil'"
          :submissionId="submissionId"
          :turnArounds="turnarounds"
          :serviceQuestions="serviceQuestions"
        />
        <project-water-submission-form
          v-else-if="sampleTypeName == 'Water'"
          :addOns="addOns"
          :managedLists="managedLists"
          :serviceQuestions="serviceQuestions"
          :submissionId="submissionId"
          :turnArounds="turnarounds"
        />
        <project-waste-submission-form
          v-else-if="sampleTypeName == 'Waste Water'"
          :addOns="addOns"
          :serviceQuestions="serviceQuestions"
          :submissionId="submissionId"
          :turnArounds="turnarounds"
        />
        <project-manure-submission-form
          v-else-if="sampleTypeName == 'Manure'"
          :submissionId="submissionId"
          :testpackageSamples="allTestPackages"
          :addOns="addOns"
          :turnArounds="turnarounds"
          :managedLists="managedLists"
          :serviceQuestions="serviceQuestions"
        />
        <project-general-submission-form
          v-else
          :submissionId="submissionId"
          :testPackageSamples="allTestPackages"
          :addOns="addOns"
          :turnArounds="turnarounds"
          :serviceQuestions="serviceQuestions"
        />
      </div>
      <b-button variant="danger" @click="removeSubmission" block>
        <i class="fal fa-trash" /> Remove Submission
      </b-button>
    </b-card>
  </div>
</template>

<script>
import AnalyteCollection from "@/collections/lab/AnalyteCollection";
import AnalyteModel from "@/models/lab/AnalyteModel";
import CustomFormRadioGroup from "@/components/shared/form/CustomFormRadioGroup";
import ProjectFeedMixerSubmissionForm from "../components/types/feedMixer/ProjectFeedMixerSubmissionForm";
import ProjectFeedsSubmissionForm from "../components/types/feeds/ProjectFeedsSubmissionForm";
import ProjectGeneralSubmissionForm from "../components/types/general/ProjectGeneralSubmissionForm";
import ProjectIrrigationSubmissionForm from "../components/types/irrigation/ProjectIrrigationSubmissionForm";
import ProjectPotatoSubmissionForm from "../components/types/potato/ProjectPotatoSubmissionForm";
import ProjectSepticSubmissionForm from "../components/types/septic/ProjectSepticSubmissionForm";
import ProjectSoilSubmissionForm from "../components/types/soil/ProjectSoilSubmissionForm";
import ProjectTissueSubmissionForm from "../components/types/tissue/ProjectTissueSubmissionForm";
import ProjectWasteSubmissionForm from "../components/types/wasteWater/ProjectWasteSubmissionForm";
import ProjectWaterSubmissionForm from "../components/types/water/ProjectWaterSubmissionForm";
import ProjectManureSubmissionForm from "../components/types/manure/ProjectManureSubmissionForm";

import { mapActions, mapGetters } from "vuex";

export default {
  name: "ProjectSubmissionForm",
  components: {
    CustomFormRadioGroup,
    ProjectPotatoSubmissionForm,
    ProjectIrrigationSubmissionForm,
    ProjectFeedMixerSubmissionForm,
    ProjectFeedsSubmissionForm,
    ProjectGeneralSubmissionForm,
    ProjectSepticSubmissionForm,
    ProjectTissueSubmissionForm,
    ProjectSoilSubmissionForm,
    ProjectWasteSubmissionForm,
    ProjectWaterSubmissionForm,
    ProjectManureSubmissionForm,
  },
  props: {
    submissionId: {
      type: String,
      default: "",
    },
    sampleType: {
      type: Object,
      default: () => null,
    },
    testPackageGroups: {
      type: Array,
      default: () => [],
    },
    serviceQuestions: {
      type: Array,
      default: () => [],
    },
    turnarounds: {
      type: Array,
      default: () => [],
    },
    managedLists: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters({
      submission: "projectFormStepTwoSubmission",
      projectInfo: "projectFormStepOne",
    }),
    sampleTypeName() {
      return this.sampleType ? this.sampleType.name : null;
    },
    testPackageModalId() {
      return `testPackageModal__${this.submissionId}`;
    },
    allTestPackages() {
      return this.testPackageGroups.reduce(
        (acc, v) => acc.concat(v.getTestPackages().models),
        []
      );
    },
    testPackageOpts() {
      return this.testPackageGroups
        .reduce((acc, cur) => {
          return acc.find(({ id }) => id === cur.id) ? acc : [...acc, cur];
        }, [])
        .map((i) => ({
          text: i.name,
          options: i
            .getTestPackages()
            .models.map((j) => ({
              text: j.name,
              value: j.id,
              icon: "cube",
            }))
            .filter((j) => {
              return !this.tp_filter || j.text.includes(this.tp_filter);
            })
            .sort((a, b) => a.text.localeCompare(b.text)),
        }))
        .filter((i) => i.options && i.options.length);
    },
    testPackage() {
      return (
        this.allTestPackages.find(
          (i) => i.id === this.submission(this.submissionId).test_package_id
        ) || null
      );
    },
    addOns() {
      const test_package_analyte_list =
        this.testPackage.test_package_samples.reduce((acc, cur) => {
          cur.test_package_analytes.forEach(
            (analyte) =>
              !acc.hasByID(new AnalyteModel(analyte)) && acc.add(analyte)
          );
          return acc;
        }, new AnalyteCollection());

      return this.allTestPackages
        .filter((i) => i.cost_add_on)
        .filter((test_package) => {
          const analyte_list = test_package.test_package_samples.reduce(
            (acc, cur) => [...acc, ...cur.test_package_analytes],
            []
          );

          return !analyte_list.some((test_package_analyte) =>
            test_package_analyte_list.models.find(
              (model) => model.analyte_id === test_package_analyte.analyte_id
            )
          );
        })
        .reduce((acc, cur) => {
          return acc.find(({ id }) => cur.id === id)
            ? acc
            : [...acc, cur]
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  data() {
    return {
      tp_filter: null,
    };
  },
  methods: {
    ...mapActions(["UPDATE_SUBMISSION", "DELETE_SUBMISSION"]),
    getSubmissionModel(testPackageId) {
      const currentTestPackage = this.allTestPackages.find(
        (item) => item.id === testPackageId
      );
      const mixedFeedSamples = Array(10).fill("");
      const formDefaults = {
        id: this.submissionId,
        identifier: null,
        event_code: null,
        test_package_id: testPackageId,
        turnaround_id: null,
        sample_type_id: this.projectInfo.sample_type_id,
        samples: currentTestPackage.test_package_samples.map(
          (sample, index) => ({
            add_ons: [],
            identifier: `Sample ${index + 1}`,
            test_package_sample_id: sample.id,
          })
        ),
      };

      switch (this.sampleTypeName) {
        case "Soil":
          return {
            id: this.submissionId,
            identifier: null,
            event_code: null,
            test_package_id: testPackageId,
            turnaround_id: null,
            sample_type_id: this.projectInfo.sample_type_id,
            zones: [
              {
                identifier: null,
                zone_id: this.$uuid.v4(),
                samples: formDefaults.samples.map((sample, index) => {
                  let start = +index * 6;

                  return {
                    ...sample,
                    sample_soil: {
                      depth_start: start,
                      depth_end: start + 6,
                      depth_total: 6,
                    },
                  };
                }),
              },
            ],
            submission_soil: {
              irrigated: 0,
              grower: null,
              projections: [
                {
                  crop: null,
                  unit: null,
                  yield: null,
                },
              ],
            },
          };
        case "Tissue":
          return {
            ...formDefaults,
            submission_tissue: {
              grower: null,
              crop: null,
              cropstage: null,
            },
          };
        case "Feed":
          return {
            ...formDefaults,
            samples: formDefaults.samples.map((sample) => {
              return {
                ...sample,
                sample_feeds: {
                  type: null,
                  animal_fed: null,
                },
              };
            }),
          };
        case "Water":
          return {
            ...formDefaults,
            samples: formDefaults.samples.map((sample) => ({
              ...sample,
              sample_water: {
                usage: null,
              },
            })),
          };
        case "Potato":
          return {
            ...formDefaults,
            submission_potato: {
              grower: null,
            },
            samples: formDefaults.samples.map((sample) => ({
              ...sample,
              sample_potato: {
                harvest_date: null,
                field: null,
              },
            })),
          };
        case "Irrigation Suitability": {
          const irrigationSamples = [];
          let counter = 0;

          while (counter < 3) {
            let start = counter * 6;
            irrigationSamples.push({
              identifier: `Sample ${counter + 1}`,
              test_package_sample_id: this.$uuid.v4(),
              field_texture: null,
              soil_horizon: null,
              depth_start: start,
              depth_end: start + 6,
            });
            counter++;
          }

          return {
            id: this.submissionId,
            identifier: null,
            event_code: null,
            test_package_id: testPackageId,
            turnaround_id: null,
            sample_type_id: this.projectInfo.sample_type_id,
            bore_holes: [
              {
                identifier: "Bore Hole 1",
                bore_hole_id: this.$uuid.v4(),
                samples: [...irrigationSamples],
              },
            ],
          };
        }
        case "Feed Mixer Efficiency":
          return {
            ...formDefaults,
            submission_feed_mixer_efficiency: {
              all_sample_types: null,
            },
            samples: mixedFeedSamples.map((sample, index) => {
              return {
                test_package_sample_id: this.$uuid.v4(),
                identifier: `Sample ${index + 1}`,
                add_ons: [],
                sample_feed_mixer_efficiency: {
                  type: null,
                },
              };
            }),
          };
        case "Septic Soil":
          return {
            ...formDefaults,
            samples: formDefaults.samples.map((sample) => ({
              ...sample,
              sample_septic: {
                depth_start: null,
                depth_end: null,
                depth_total: null,
                sieve: false,
                sar: false,
                ec: false,
                cole: false,
                dispersion: false,
              },
            })),
          };
        default:
          return formDefaults;
      }
    },
    updateTestPackage(testPackageId) {
      const currentSubmission = this.submission(this.submissionId);
      const submission = currentSubmission && currentSubmission.test_package_id ?
        {
          ...currentSubmission,
          test_package_id: testPackageId,
        } :
        this.getSubmissionModel(testPackageId)

      this.UPDATE_SUBMISSION({
        id: this.submissionId,
        value: {
          ...submission
        },
      });
      this.$bvModal.hide(this.testPackageModalId);
    },
    removeSubmission() {
      this.DELETE_SUBMISSION(this.submissionId);
    },
  },
};
</script>
